import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Shield, ShieldCheck, ShieldOff, Loader2, AlertTriangle } from "lucide-react";

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: any;
  onSuccess: () => void;
}

const AVAILABLE_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'site_admin', label: 'Site Admin' },
  { value: 'chair', label: 'Chair' },
  { value: 'treasurer', label: 'Treasurer' },
  { value: 'secretary', label: 'Secretary' },
  { value: 'trustee', label: 'Trustee' },
  { value: 'school_liaison', label: 'School Liaison' },
  { value: 'communications', label: 'Communications' },
  { value: 'catering', label: 'Catering' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'user', label: 'User' },
];

export function EditUserDialog({ open, onOpenChange, user, onSuccess }: EditUserDialogProps) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [adminNotes, setAdminNotes] = useState("");
  const [loading, setLoading] = useState(false);
  
  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [checkingMfa, setCheckingMfa] = useState(false);
  const [showDisableMfaDialog, setShowDisableMfaDialog] = useState(false);
  const [disableReason, setDisableReason] = useState("");
  const [disablingMfa, setDisablingMfa] = useState(false);

  // Log profile access for audit
  const logProfileAccess = async (actionType: 'view' | 'edit') => {
    if (!user?.user_id) return;
    
    try {
      await supabase.functions.invoke('log-profile-access', {
        body: {
          accessed_profile_id: user.user_id,
          accessed_profile_email: user.email,
          action_type: actionType,
        },
      });
    } catch (err) {
      console.error('Failed to log profile access:', err);
      // Don't block the user action if logging fails
    }
  };

  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        setFullName(user.full_name || "");
        setEmail(user.email || "");
        setAdminNotes(user.admin_notes || "");
        
        // Log view access when dialog opens
        logProfileAccess('view');
        
        // Fetch roles using the actual user_id from auth, not the member id
        if (user.user_id) {
          const { data: rolesData } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", user.user_id);
          
          setSelectedRoles(rolesData?.map((r: any) => r.role) || []);
          
          // Check MFA status via edge function
          checkMfaStatus();
        } else {
          setSelectedRoles([]);
          setMfaEnabled(false);
        }
      }
    };
    
    loadUserData();
  }, [user]);

  const checkMfaStatus = async () => {
    if (!user?.user_id) return;
    
    setCheckingMfa(true);
    try {
      // We can't directly check MFA status from client without admin API
      // So we'll check via a simple heuristic: try to see if user has factors
      // This will be properly checked when admin tries to disable
      setMfaEnabled(false); // Default to false, will show "Unknown" if we can't determine
      
      // For now, we'll let the disable action check and handle appropriately
      // The edge function will return error if no MFA is enabled
    } catch (err) {
      console.error('Error checking MFA status:', err);
    } finally {
      setCheckingMfa(false);
    }
  };

  const handleDisableMfa = async () => {
    if (!user?.user_id) return;
    
    setDisablingMfa(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-disable-mfa', {
        body: {
          targetUserId: user.user_id,
          targetUserEmail: user.email,
          reason: disableReason || 'No reason provided',
        },
      });

      if (error) throw error;
      
      if (data.error) {
        if (data.error === 'User does not have MFA enabled') {
          toast({
            title: "No MFA Found",
            description: "This user does not have MFA enabled.",
          });
        } else {
          throw new Error(data.error);
        }
      } else {
        toast({
          title: "MFA Disabled",
          description: `MFA has been disabled for ${user.email}. They can now log in with just their password.`,
        });
        setMfaEnabled(false);
      }

      setShowDisableMfaDialog(false);
      setDisableReason("");
    } catch (err: any) {
      console.error('Error disabling MFA:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to disable MFA",
        variant: "destructive",
      });
    } finally {
      setDisablingMfa(false);
    }
  };

  const toggleRole = (role: string) => {
    setSelectedRoles(prev => 
      prev.includes(role) 
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Update profile
    const { error: profileError } = await supabase
      .from('profiles')
      .update({
        full_name: fullName,
        email: email
      })
      .eq('id', user.user_id);

    if (profileError) {
      setLoading(false);
      toast({
        title: "Error",
        description: profileError.message,
        variant: "destructive"
      });
      return;
    }

    // Update admin notes in members table
    const { error: notesError } = await supabase
      .from('members')
      .update({
        admin_notes: adminNotes
      })
      .eq('id', user.id);

    if (notesError) {
      setLoading(false);
      toast({
        title: "Error",
        description: notesError.message,
        variant: "destructive"
      });
      return;
    }

    // Get current roles from database using user_id
    const { data: currentRolesData } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.user_id);
    
    const currentRoles = currentRolesData?.map((r: any) => r.role) || [];
    
    console.log('=== ROLE SAVE DEBUG ===');
    console.log('User ID:', user.user_id);
    console.log('Current roles:', currentRoles);
    console.log('Selected roles:', selectedRoles);
    
    // Roles to add
    const rolesToAdd = selectedRoles.filter(role => !currentRoles.includes(role));
    
    // Roles to remove
    const rolesToRemove = currentRoles.filter((role: string) => !selectedRoles.includes(role));

    console.log('Roles to add:', rolesToAdd);
    console.log('Roles to remove:', rolesToRemove);

    // Add new roles
    if (rolesToAdd.length > 0) {
      console.log('Inserting roles:', rolesToAdd.map(role => ({ user_id: user.user_id, role })));
      const { data: insertedData, error: addError } = await supabase
        .from('user_roles')
        .insert(rolesToAdd.map(role => ({ user_id: user.user_id, role: role as any })))
        .select();

      console.log('Insert result:', { insertedData, addError });

      if (addError) {
        setLoading(false);
        toast({
          title: "Error",
          description: addError.message,
          variant: "destructive"
        });
        return;
      }
    }

    // Remove old roles
    if (rolesToRemove.length > 0) {
      console.log('Deleting roles:', rolesToRemove);
      const { error: removeError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', user.user_id)
        .in('role', rolesToRemove);

      console.log('Delete result:', { removeError });

      if (removeError) {
        setLoading(false);
        toast({
          title: "Error",
          description: removeError.message,
          variant: "destructive"
        });
        return;
      }
    }

    // Log edit access
    await logProfileAccess('edit');

    setLoading(false);
    toast({
      title: "Success",
      description: "User updated successfully"
    });
    onSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>Update user information</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Full Name</Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="John Smith"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              {AVAILABLE_ROLES.map(role => (
                <div key={role.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={role.value}
                    checked={selectedRoles.includes(role.value)}
                    onCheckedChange={() => toggleRole(role.value)}
                  />
                  <label
                    htmlFor={role.value}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {role.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="adminNotes">Site Admin Notes</Label>
            <p className="text-xs text-muted-foreground">Internal notes about this user (only visible to site admins)</p>
            <Textarea
              id="adminNotes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add notes about this user (e.g., warnings, reports, behavioral issues)..."
              rows={4}
              className="resize-none"
            />
          </div>

          <Separator />

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              <Label>Security</Label>
            </div>
            <div className="p-3 border rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Two-Factor Authentication</span>
                {checkingMfa ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    Check on disable
                  </Badge>
                )}
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setShowDisableMfaDialog(true)}
                className="w-full"
              >
                <ShieldOff className="mr-2 h-4 w-4" />
                Disable MFA for User
              </Button>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                This action is logged for audit purposes
              </p>
            </div>
          </div>

          <AlertDialog open={showDisableMfaDialog} onOpenChange={setShowDisableMfaDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Disable MFA for {user?.email}?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will remove two-factor authentication from this user's account, allowing them to log in with just their password.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="py-4">
                <Label htmlFor="disableReason">Reason (optional)</Label>
                <Textarea
                  id="disableReason"
                  value={disableReason}
                  onChange={(e) => setDisableReason(e.target.value)}
                  placeholder="e.g., User lost access to authenticator app"
                  className="mt-2"
                />
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDisableMfa}
                  disabled={disablingMfa}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {disablingMfa ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Disabling...
                    </>
                  ) : (
                    "Disable MFA"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
